import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import { deleteEventPermanently, getTrashedEvents, restoreEvent, type TrashedEvent } from "@/services/api/events";
import { formatDateShort } from "@/utils/eventUtils";
import { eventPlanOf } from "@/utils/purchaseEvent";
import { PermanentDeleteDialog } from "./PermanentDeleteDialog";

interface TrashSectionProps {
  currentLocale: string;
  /** Changes whenever the dashboard deleted something, to refresh the list. */
  refreshKey: number;
  /** Called after a restore so the dashboard reloads its events. */
  onRestored: () => void;
}

/**
 * JUB-157: "Coș de gunoi (N)" — events deleted in the last 30 days, with
 * Restore (plan comes back) and Delete permanently (type the name).
 */
export function TrashSection({ currentLocale, refreshKey, onRestored }: TrashSectionProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<TrashedEvent[]>([]);
  const [open, setOpen] = useState(false);
  const [busyUuid, setBusyUuid] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TrashedEvent | null>(null);

  const load = useCallback(() => {
    getTrashedEvents()
      .then(setItems)
      .catch(() => toast.error(t("events.trash.loadError")));
  }, [t]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (items.length === 0) {
    return null;
  }

  const handleRestore = async (event: TrashedEvent) => {
    setBusyUuid(event.uuid);
    try {
      await restoreEvent(event.uuid);
      toast.success(t("events.trash.restored"));
      load();
      onRestored();
    } catch {
      toast.error(t("events.trash.restoreError"));
    } finally {
      setBusyUuid(null);
    }
  };

  const handlePermanentDelete = async () => {
    if (!pendingDelete) return;
    setBusyUuid(pendingDelete.uuid);
    try {
      await deleteEventPermanently(pendingDelete.uuid);
      toast.success(t("events.trash.permanentDeleted"));
      setPendingDelete(null);
      load();
    } catch {
      toast.error(t("events.trash.permanentError"));
    } finally {
      setBusyUuid(null);
    }
  };

  return (
    <section className="mt-10 rounded-lg border border-dashed" data-testid="trash-section">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-left font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 rounded-lg"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid="trash-toggle"
      >
        <span className="flex items-center gap-2">
          <Trash2 className="h-4 w-4 text-muted-foreground" />
          {t("events.trash.title", { count: String(items.length) })}
        </span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
        <div className="space-y-3 px-4 pb-4">
          <p className="text-sm text-muted-foreground">{t("events.trash.description")}</p>
          <ul className="space-y-2">
            {items.map((event) => {
              const plan = eventPlanOf(event);
              return (
                <li
                  key={event.uuid}
                  className="flex flex-col gap-3 rounded-md border bg-background p-3 sm:flex-row sm:items-center sm:justify-between"
                  data-testid="trash-item"
                >
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-medium">
                      <span className="truncate">{event.name}</span>
                      {plan !== "free" && (
                        <Badge variant="secondary" data-testid="trash-item-plan">
                          {t(`landing.pricing.${plan}.name`) || plan}
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {event.restorable && event.retentionUntil
                        ? t("events.trash.purgesOn", { date: formatDateShort(event.retentionUntil, currentLocale) })
                        : t("events.trash.expired")}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(event)}
                      disabled={!event.restorable || busyUuid === event.uuid}
                      data-testid="trash-restore-button"
                    >
                      <RotateCcw className="mr-1 h-3.5 w-3.5" />
                      {t("events.trash.restore")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setPendingDelete(event)}
                      disabled={busyUuid === event.uuid}
                      data-testid="trash-delete-permanently-button"
                    >
                      {t("events.trash.deletePermanently")}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <PermanentDeleteDialog
        eventName={pendingDelete?.name ?? null}
        busy={busyUuid !== null}
        onConfirm={handlePermanentDelete}
        onOpenChange={(isOpen) => !isOpen && setPendingDelete(null)}
      />
    </section>
  );
}
